import React from 'react';
import { Grid, Button, Chip, Stack, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCanOpen } from '../../context/PageAccessContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccountManagerCard from '../AccountManagerCard';
import FamilyContactsCard from '../FamilyContactsCard';
import {
  DashboardHeader, StatTile, KpiRow, Section, Meter, RowList, AttentionList, StatusChip, rupees, fmtDate, fmtDateTime, LinkButton,
} from './DashboardParts';

const WISHLIST_STATUS = {
  draft: 'Draft', pending_confirmation: 'Awaiting confirmation', pending_approval: 'Awaiting admin approval',
  approved: 'Approved', rejected: 'Not approved', contracted: 'Contract created',
};

export default function FamilyDashboard({ data }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const k = data.kpis;
  const journey = data.journey;
  const sideLabel = data.side === 'bride' ? "Bride's family" : "Groom's family";

  return (
    <>
      <DashboardHeader
        title={`Welcome, ${data.name}`}
        subtitle="Your wedding planning at a glance"
        chip={<Chip color={data.side === 'bride' ? 'secondary' : 'info'} label={sideLabel} />}
      />
      <KpiRow>
        <StatTile hero label="Journey" value={`${journey.completed}/${journey.steps.length}`} sub="steps completed" />
        <StatTile label="Guests" value={k.guests} sub={`${k.confirmedGuests} confirmed · ${k.pendingGuests} pending`} to="/dashboard/guests" />
        <StatTile label="Vendors booked" value={k.vendorsBooked} sub={k.vendorsAwaiting ? `${k.vendorsAwaiting} awaiting confirmation` : `${k.servicesSelected} services selected`} to="/dashboard/my-vendors" />
        <StatTile
          label="Next ceremony"
          value={k.nextCeremony ? fmtDate(k.nextCeremony.event_date) : '—'}
          sub={k.nextCeremony ? k.nextCeremony.name : 'No upcoming ceremonies'}
          to="/dashboard/rituals"
        />
      </KpiRow>

      <AccountManagerCard sx={{ mb: 2 }} />
      <FamilyContactsCard compact onOpen={() => navigate('/dashboard/profile#contacts')} sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Section title="Your journey">
            <Stack spacing={1}>
              {journey.steps.map(s => (
                <Stack key={s.key} direction="row" spacing={1.5} alignItems="center"
                  onClick={canOpen(s.link) ? () => navigate(s.link) : undefined}
                  sx={{ cursor: canOpen(s.link) ? 'pointer' : 'default', p: 1, borderRadius: 1, '&:hover': canOpen(s.link) ? { bgcolor: 'action.hover' } : undefined }}>
                  {s.done ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color="disabled" />}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.detail}</Typography>
                  </Box>
                  <Typography variant="caption" color={s.done ? 'success.main' : 'text.secondary'}>{s.done ? 'Done' : 'To do'}</Typography>
                </Stack>
              ))}
            </Stack>
          </Section>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Section title="Needs your attention"><AttentionList items={data.attention} /></Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Guests & RSVPs" action={<LinkButton to="/dashboard/guests">Guest list</LinkButton>}>
            <Meter label="Confirmed" value={k.confirmedGuests} total={k.guests} />
            <Meter label="Profile complete" value={k.profileCompletion} total={100} caption={`${k.profileCompletion}%`} />
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${k.pendingGuests} pending`} />
              <Chip size="small" label={`${k.declinedGuests} declined`} />
              <Chip size="small" label={`${k.vipGuests} VIP`} />
            </Stack>
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Budget" action={<LinkButton to="/dashboard/budget">Open budget</LinkButton>}>
            {k.budgetItems ? (
              <>
                <Meter label="Spent against plan" value={k.budgetActual} total={k.budgetPlanned} caption={`${rupees(k.budgetActual)} of ${rupees(k.budgetPlanned)}`} />
                <Meter label="Paid so far" value={k.budgetPaid} total={k.budgetActual || k.budgetPlanned} caption={rupees(k.budgetPaid)} />
              </>
            ) : <Typography variant="body2" color="text.secondary">No budget items yet — add them to track spending.</Typography>}
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title="Your vendors" action={<LinkButton to="/dashboard/my-vendors">My Vendors</LinkButton>}>
            <RowList
              rows={data.bookings.map(b => ({
                primary: `${b.business_name} · ${b.offering_title}${b.package_name ? ` – ${b.package_name}` : ''}`,
                secondary: `${b.category_label} · ${fmtDate(b.required_date)}${b.required_time ? `, ${b.required_time}` : ''} · ${b.days_until === 0 ? 'today' : `in ${b.days_until} day(s)`}`,
                right: <Chip size="small" color={b.status === 'confirmed' ? 'success' : 'warning'} label={b.status === 'confirmed' ? 'Confirmed' : 'Awaiting vendor'} />,
              }))}
              empty="No vendors booked yet — open Service Providers and click Book on the vendors you like."
            />
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Your services" action={<LinkButton to="/dashboard/service-providers">Find providers</LinkButton>}>
            <RowList
              rows={data.services.map(s => ({
                primary: s.label,
                secondary: s.booked ? `Booked: ${s.booked.business_name} on ${fmtDate(s.booked.required_date)}` : undefined,
                right: s.booked
                  ? <Chip size="small" color={s.booked.status === 'confirmed' ? 'success' : 'warning'} label={s.booked.status === 'confirmed' ? 'Vendor booked' : 'Awaiting vendor'} />
                  : !s.bookable
                    // Vendors open only once the contract covering this service is signed
                    ? <Chip size="small" variant="outlined" label="Sign contract to book" />
                    : <Chip size="small" color={s.providers ? 'primary' : 'default'} variant="outlined"
                      label={s.providers ? `${s.providers} provider${s.providers === 1 ? '' : 's'} available` : 'No providers yet'} />,
              }))}
              empty="No services selected yet — use the Service Wizard to choose them."
            />
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Contracts & wishlists" action={<LinkButton to="/dashboard/contracts">Contracts</LinkButton>}>
            <RowList
              rows={[
                ...data.contracts.map(c => ({ primary: c.title, secondary: c.ref, right: <StatusChip status={c.status} /> })),
                ...data.wishlists.map(w => ({
                  primary: `Service wishlist (${w.services} categories)`, secondary: w.event_date ? `Event ${fmtDate(w.event_date)}` : fmtDate(w.created_at),
                  right: <Chip size="small" variant="outlined" label={WISHLIST_STATUS[w.status] || w.status} />,
                })),
              ]}
              empty="No contracts or wishlists yet."
            />
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Matchmaking" action={<LinkButton to="/dashboard/matchmaking">Open</LinkButton>}>
            <Grid container spacing={1}>
              {[['Interests sent', k.interestsSent], ['Received', k.interestsReceived], ['Accepted', k.interestsAccepted], ['Shortlisted', k.shortlisted]].map(([l, v]) => (
                <Grid item xs={6} key={l}>
                  <Typography variant="caption" color="text.secondary">{l}</Typography>
                  <Typography variant="h6" fontWeight={700}>{v}</Typography>
                </Grid>
              ))}
            </Grid>
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Ceremonies & meetings">
            <RowList
              rows={[
                ...data.ceremonies.map(c => ({ primary: c.name, secondary: [fmtDate(c.event_date), c.venue].filter(Boolean).join(' · ') })),
                ...data.upcomingMeetings.map(m => ({ primary: m.title, secondary: fmtDateTime(m.proposed_at), right: <Chip size="small" label="Meeting" /> })),
              ]}
              empty="No ceremonies or meetings scheduled."
            />
          </Section>
        </Grid>
      </Grid>
    </>
  );
}
